import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowLeftIcon, RefreshCwIcon } from 'lucide-react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { creditWithYen, numberText, planLabels } from '@/components/credit-contracts/display';
import { fillViewportSx } from '@/components/credit-contracts/layout';
import { RowDialog } from '@/components/credit-contracts/RowDialog';
import { AddButton, DataConnectTable, PlanTable } from '@/components/credit-contracts/RowTables';
import { useContractDetail } from '@/components/credit-contracts/useContractDetail';
import type { ContractDetail, ContractTable } from '@/lib/api/credit-contracts';
import { contains, formatPeriod, monthLabel } from '@/lib/credit-contracts/month';

/** URL の tab の値。契約料金プランは tab を付けない */
const DATA_CONNECT_TAB: ContractTable = 'data-connect-credits';

/**
 * 契約詳細（FKV3-13045）。契約一覧で企業を選ぶと移る。
 * 1つの企業について、契約料金プランとデータコネクトのクレジット数の行を期間ごとに表示し、追加・編集・削除する。
 * 2つの一覧はタブで切り替え、どちらも画面の高さに収めて、見出しの行を固定し値の行だけをスクロールする。
 */
export function ContractDetailPage() {
  const { companyId = '' } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const s = useContractDetail(companyId);
  // 契約一覧から移ったときは、指定した月と絞り込みを移る前のままにして戻る（契約一覧のクエリは URL の list に持つ）
  const list = searchParams.get('list');
  const listSearch = list ? `?${list}` : '';
  // 消費詳細から移ったときは、戻る先を消費詳細にする（管理画面の中の消費詳細のパスだけを受け付ける）
  const back = searchParams.get('back');
  const backToConsumption = back?.startsWith('/credit/consumption/') ? back : null;
  const detail = s.detail.status === 'ok' ? s.detail.data : null;
  const disabled = s.busy || s.refreshing;
  // 開いているタブは URL に持ち、再読み込みや保存の後も同じタブを示す
  const tab: ContractTable =
    searchParams.get('tab') === DATA_CONNECT_TAB ? DATA_CONNECT_TAB : 'pricing-plans';
  const selectTab = (next: ContractTable) => {
    const params = new URLSearchParams(searchParams);
    if (next === DATA_CONNECT_TAB) params.set('tab', DATA_CONNECT_TAB);
    else params.delete('tab');
    setSearchParams(params, { replace: true, state: location.state });
  };

  return (
    <AppLayout title="契約管理 / 契約詳細" showSearch={false} disableContentPadding>
      <Box display="flex" flexDirection="column" gap={1.5} sx={fillViewportSx}>
        <Box display="flex" alignItems="baseline" columnGap={2} flexWrap="wrap" flexShrink={0}>
          {backToConsumption ? (
            <Link
              to={backToConsumption}
              className="inline-flex items-center gap-1 self-center text-sm text-blue-700 hover:underline"
            >
              <ArrowLeftIcon size={16} />
              消費詳細へ
            </Link>
          ) : (
            <Link
              to={`/credit/contracts${listSearch}`}
              state={{ focusCompany: companyId }}
              className="inline-flex items-center gap-1 self-center text-sm text-blue-700 hover:underline"
            >
              <ArrowLeftIcon size={16} />
              契約一覧へ
            </Link>
          )}
          {detail && (
            <>
              <Typography variant="subtitle1" component="h2" fontWeight="bold">
                {detail.company.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detail.company.id}
              </Typography>
            </>
          )}
        </Box>

        {s.detail.status === 'loading' && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {s.detail.status === 'error' && (
          <Alert
            severity="error"
            action={
              <Button size="sm" variant="outline" onClick={() => void s.load()}>
                <RefreshCwIcon />
                再読み込み
              </Button>
            }
          >
            {s.detail.message}
          </Alert>
        )}
        {detail && (
          <>
            <CurrentContract detail={detail} />
            <Paper
              variant="outlined"
              sx={{
                position: 'relative',
                flex: '0 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {s.refreshing && (
                <LinearProgress
                  aria-label="取得中"
                  sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }}
                />
              )}
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                px={2}
                borderBottom={1}
                borderColor="divider"
                flexShrink={0}
              >
                <Tabs
                  value={tab}
                  onChange={(_, next: ContractTable) => selectTab(next)}
                  sx={{ flex: 1, minWidth: 0, minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}
                >
                  <Tab
                    value="pricing-plans"
                    label="契約料金プラン"
                    id="contract-tab-pricing-plans"
                    aria-controls="contract-tabpanel"
                  />
                  <Tab
                    value={DATA_CONNECT_TAB}
                    label="データコネクト"
                    id="contract-tab-data-connect-credits"
                    aria-controls="contract-tabpanel"
                  />
                </Tabs>
                <AddButton disabled={disabled} onClick={() => s.openCreate(tab)} />
              </Box>
              <Box
                role="tabpanel"
                id="contract-tabpanel"
                aria-labelledby={`contract-tab-${tab}`}
                sx={{ flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
              >
                {tab === 'pricing-plans' ? (
                  <PlanTable
                    detail={detail}
                    disabled={disabled}
                    onEdit={(row) => s.openEdit('pricing-plans', row)}
                    onDelete={(row) => s.openDelete('pricing-plans', row)}
                  />
                ) : (
                  <DataConnectTable
                    detail={detail}
                    disabled={disabled}
                    onEdit={(row) => s.openEdit('data-connect-credits', row)}
                    onDelete={(row) => s.openDelete('data-connect-credits', row)}
                  />
                )}
              </Box>
            </Paper>
            <RowDialog
              dialog={s.dialog}
              detail={detail}
              busy={s.busy}
              onChange={s.change}
              onConfirm={s.toConfirm}
              onBack={s.backToInput}
              onSave={() => void s.save()}
              onDelete={() => void s.remove()}
              onClose={s.close}
              onRefetch={s.refetch}
            />
          </>
        )}
      </Box>
    </AppLayout>
  );
}

/** 当月の契約料金プランとデータコネクトのクレジット数を、それぞれの期間とあわせて1行で示す */
function CurrentContract({ detail }: { detail: ContractDetail }) {
  const plan = detail.pricing_plans.find((r) => contains(r, detail.current_month));
  const dc = detail.data_connect_credits.find((r) => contains(r, detail.current_month));
  const planText = !plan
    ? 'なし'
    : plan.pricing_plan === 'Credit'
      ? `${planLabels.Credit}　${numberText(plan.monthly_contract_credit)}クレジット/月 × ${numberText(plan.credit_unit_price)}円/クレジット`
      : `${planLabels.FixedFee}　月額料金 ${numberText(plan.monthly_fee)}円`;
  const item = (term: string, value: string) => (
    <Box display="flex" columnGap={1}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {term}
      </Typography>
      <Typography component="dd" variant="body2" m={0}>
        {value}
      </Typography>
    </Box>
  );
  return (
    <Box
      component="dl"
      display="flex"
      alignItems="baseline"
      columnGap={3}
      rowGap={0.5}
      flexWrap="wrap"
      m={0}
      flexShrink={0}
    >
      <Typography component="h3" variant="body2" fontWeight="bold">
        当月 {monthLabel(detail.current_month)}
      </Typography>
      {item('契約料金プラン', plan ? `${planText}（${formatPeriod(plan)}）` : planText)}
      {item(
        'データコネクト',
        dc
          ? `${creditWithYen(dc.monthly_credit, dc.start_month, detail.credit_face_values)}（${formatPeriod(dc)}）`
          : 'なし',
      )}
    </Box>
  );
}
